import { useState, useCallback } from 'react';
import { AddressService } from '../../../services/address';

interface AddressResult {
  street: string;
  district: string;
  city: string;
  state: string;
}

interface UseAddressLookupReturn {
  lookup: (zipCode: string, userId?: string) => Promise<AddressResult | null>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

export const useAddressLookup = (): UseAddressLookupReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const lookup = useCallback(async (zipCode: string, userId?: string): Promise<AddressResult | null> => {
    // Remove non-numeric characters
    const cleanZipCode = zipCode.replace(/\D/g, '');

    // Validate zip code length
    if (cleanZipCode.length !== 8) {
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await AddressService.getAddressByZipCode(cleanZipCode, userId);

      if (!response) {
        setError('CEP não encontrado');
        return null;
      }

      return {
        street: response.street || '',
        district: response.district || '',
        city: response.city || '',
        state: response.state || '',
      };
    } catch (err) {
      console.error('Address lookup error:', err);
      const errorMessage = err instanceof Error ? err.message : 'CEP não encontrado. Verifique e tente novamente.';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    lookup,
    isLoading,
    error,
    clearError,
  };
};

export default useAddressLookup;
